/**
 * =============================================================================
 * ROASTBREW - LIBRARY PAGE (Content Only)
 * =============================================================================
 * 
 * Premium video library interface with featured content, video rows, and search.
 * This component renders ONLY the main content area - the sidebar is handled
 * by AppLayout which wraps this component.
 * 
 * PROPS:
 * @param {object} user - Current user { name, email }
 * @param {string} currentTier - Subscription tier ('free', 'pro', 'pro_plus')
 * @param {object} colors - Theme colors from parent
 * @param {boolean} isMobile - Viewport is mobile (passed from AppLayout)
 * @param {boolean} isTablet - Viewport is tablet (passed from AppLayout)
 * @param {boolean} isDesktop - Viewport is desktop (passed from AppLayout)
 * 
 * =============================================================================
 */

import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Hls from 'hls.js';
import { useTheme } from './ThemeContext';
import { useAuth } from './AuthContext';
import { videoPct, videoResume } from './data/progress';
import { isLightTheme as tierIsLight, isLocked } from './config';
import SearchBar from './SearchBar';
import UpgradePrompt from './UpgradePrompt';




// =============================================================================
// SUB-COMPONENTS
// =============================================================================

// Lock badge for videos the user's plan doesn't include. Same look as search.
function LockBadge({ isMobile }) {
  return (
    <div style={{
      position: 'absolute', top: isMobile ? '8px' : '10px', right: isMobile ? '8px' : '10px', zIndex: 3,
      display: 'inline-flex', alignItems: 'center', gap: '5px',
      padding: isMobile ? '3px 7px' : '4px 9px', borderRadius: '4px',
      background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,255,255,0.14)',
      fontSize: isMobile ? '9px' : '10px', fontWeight: '700', letterSpacing: '0.06em', textTransform: 'uppercase',
      fontFamily: '"DM Sans", sans-serif', color: '#FFFFFF', pointerEvents: 'none',
    }}>
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#FFFFFF" strokeWidth="2" aria-hidden="true">
        <rect x="5" y="11" width="14" height="9" rx="2" /><path d="M8 11V8a4 4 0 0 1 8 0v3" />
      </svg>
      Locked
    </div>
  );
}

function HeroSection({ feature, colors, currentTier, isMobile, isTablet, onNavigate }) {
  const [isHovered, setIsHovered] = useState(false);
  const { progressByVideo } = useAuth();
  const heroPct = feature ? videoPct(progressByVideo, feature.videoId) : 0;
  const isLight = tierIsLight(currentTier);
  if (!feature) return null;

  const go = () => onNavigate && onNavigate(feature);
  const shouldPreload = !prefersReducedData();

  return (
    <section
      onClick={go}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        height: isMobile ? '380px' : isTablet ? '420px' : '480px',
        marginBottom: isMobile ? '32px' : '48px',
        borderRadius: isMobile ? '14px' : '18px',
        overflow: 'hidden',
        cursor: 'pointer',
        border: `1px solid ${isLight ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.08)'}`,
        boxShadow: isHovered
          ? `0 28px 64px -22px ${colors.accentGlow}`
          : (isLight ? '0 14px 36px -18px rgba(0,0,0,0.20)' : '0 14px 36px -18px rgba(0,0,0,0.55)'),
        transition: 'box-shadow 0.4s ease',
      }}
    >
      {/* Full-bleed video background: poster always, preview on hover, subtle zoom */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 0,
        transform: isHovered && !isMobile ? 'scale(1.05)' : 'scale(1)',
        transition: 'transform 0.7s cubic-bezier(0.4,0,0.2,1)',
      }}>
        <img src={feature.thumbnailUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        {feature.previewUrl && (shouldPreload || isHovered) && (
          <HoverPreview src={feature.previewUrl} objectPosition="top" highQuality active={isHovered} maxBufferSeconds={20} resumeFrom={videoResume(progressByVideo, feature.videoId)} />
        )}
      </div>

      {/* Black filter: light overall so the video reads, weighted darker toward the
          bottom-left where the text sits, for legibility */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none',
        background: `
          linear-gradient(0deg, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.45) 36%, rgba(0,0,0,0.22) 68%, rgba(0,0,0,0.32) 100%),
          linear-gradient(90deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.15) 55%, transparent 100%)
        `,
      }} />

      {feature.locked && <LockBadge isMobile={isMobile} />}
      {/* Content: cinematic bottom-left */}
      <div style={{
        position: 'absolute', inset: 0, zIndex: 2,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
        padding: isMobile ? '24px' : isTablet ? '40px 44px' : '52px 60px',
        maxWidth: isMobile ? '100%' : '680px',
      }}>
        <span style={{
          alignSelf: 'flex-start',
          padding: isMobile ? '4px 10px' : '6px 14px',
          background: colors.accent,
          borderRadius: '4px',
          fontSize: isMobile ? '10px' : '11px',
          fontWeight: '700',
          color: '#1A1A1A',
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontFamily: '"DM Sans", sans-serif',
          marginBottom: isMobile ? '12px' : '16px',
        }}>
          Recently Watched
        </span>

        <h1 style={{
          fontSize: isMobile ? '32px' : isTablet ? '44px' : '56px',
          fontWeight: '400',
          color: '#FFFFFF',
          fontFamily: '"Cormorant Garamond", serif',
          lineHeight: 1.05,
          letterSpacing: '-0.01em',
          margin: 0,
          textShadow: '0 2px 16px rgba(0,0,0,0.5)',
        }}>
          {feature.title}
        </h1>

        <p style={{
          fontSize: isMobile ? '16px' : '20px',
          fontWeight: '300',
          color: colors.accent,
          fontFamily: '"Cormorant Garamond", serif',
          fontStyle: 'italic',
          margin: isMobile ? '6px 0 0' : '8px 0 0',
          textShadow: '0 1px 10px rgba(0,0,0,0.5)',
        }}>
          {feature.series}
        </p>

        {!isMobile && (
          <p style={{
            fontSize: '15px',
            color: 'rgba(255,255,255,0.8)',
            fontFamily: '"DM Sans", sans-serif',
            lineHeight: 1.6,
            margin: '16px 0 0',
            maxWidth: '460px',
            textShadow: '0 1px 8px rgba(0,0,0,0.4)',
          }}>
            Jump back into where you left off and keep your momentum going.
          </p>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: isMobile ? '20px' : '26px' }}>
          <button onClick={(e) => { e.stopPropagation(); go(); }} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            padding: isMobile ? '13px 22px' : '15px 30px',
            background: colors.accent,
            border: 'none',
            borderRadius: '10px',
            fontSize: isMobile ? '14px' : '15px',
            fontWeight: '700',
            color: '#1A1A1A',
            fontFamily: '"DM Sans", sans-serif',
            cursor: 'pointer',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: isHovered ? `0 10px 28px -8px ${colors.accentGlow}` : '0 6px 18px -8px rgba(0,0,0,0.5)',
            transform: isHovered && !isMobile ? 'translateY(-1px)' : 'translateY(0)',
            width: isMobile ? '100%' : 'auto',
          }}>
            <svg width={isMobile ? 16 : 18} height={isMobile ? 16 : 18} viewBox="0 0 24 24" fill="#1A1A1A"><path d="M8 5v14l11-7z" /></svg>
            <span>Watch Again</span>
          </button>
        </div>
      </div>

      {/* Resume progress across the very bottom */}
      {heroPct > 0 ? (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: isMobile ? '4px' : '5px', background: 'rgba(0,0,0,0.5)', zIndex: 3 }}>
          <div style={{ height: '100%', width: `${heroPct}%`, background: colors.accent, boxShadow: `0 0 12px ${colors.accent}`, transition: 'width 0.3s ease' }} />
        </div>
      ) : null}
    </section>
  );
}


// Browsers block playing media WITH SOUND until the user has interacted with the
// page at least once (a hover doesn't count — only click / key / tap). We watch
// for the first such gesture and flip this flag so hover previews start unmuted
// from then on. Reaching the Library requires a click, so it unlocks immediately.
let AUDIO_UNLOCKED = false;
if (typeof window !== 'undefined') {
  const unlock = () => {
    AUDIO_UNLOCKED = true;
    window.removeEventListener('pointerdown', unlock, true);
    window.removeEventListener('keydown', unlock, true);
    window.removeEventListener('touchstart', unlock, true);
  };
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('touchstart', unlock, true);
}

// Skip the hero's background preload on data-saver or very slow connections, so we
// don't pull a high-bitrate stream on every Library load where it hurts most.
function prefersReducedData() {
  if (typeof navigator === 'undefined') return false;
  const c = navigator.connection;
  if (!c) return false;
  if (c.saveData) return true;
  return /(^|-)2g$/.test(c.effectiveType || '');   // '2g' or 'slow-2g'
}

function HoverPreview({ src, objectPosition = 'center', highQuality = false, active = true, maxBufferSeconds = 30, resumeFrom = 0 }) {
  const ref = useRef(null);

  // Set up HLS (or native) once and begin buffering immediately. When the parent
  // keeps this element mounted (the hero), buffering happens in the background on
  // page load; maxBufferLength caps how much gets pre-fetched.
  useEffect(() => {
    const v = ref.current;
    if (!v || !src) return undefined;
    const seekOnce = () => {
      if (resumeFrom > 0 && Number.isFinite(v.duration) && resumeFrom < v.duration) {
        try { v.currentTime = resumeFrom; } catch (e) { /* noop */ }
      }
    };
    v.addEventListener('loadedmetadata', seekOnce, { once: true });
    let hls;
    const isHls = /\.m3u8(\?|$)/i.test(src);
    if (isHls && Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        capLevelToPlayerSize: !highQuality,
        abrEwmaDefaultEstimate: 8000000,
        maxBufferLength: maxBufferSeconds,   // cap the (pre)buffer, in seconds
        startPosition: resumeFrom > 0 ? resumeFrom : -1,   // begin at the resume point
      });
      hls.attachMedia(v);
      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(src));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        if (highQuality && hls.levels && hls.levels.length) {
          // Scan the variants parsed from the master .m3u8, pick the highest-bitrate
          // one, and pin to it so playback starts at top quality (no low-res ramp).
          let top = 0, max = -1;
          hls.levels.forEach((lvl, i) => { if (lvl.bitrate > max) { max = lvl.bitrate; top = i; } });
          hls.autoLevelCapping = -1;
          hls.startLevel = top;
          hls.currentLevel = top;
        }
      });
    } else {
      v.src = src;   // native HLS (Safari) or plain mp4
    }
    return () => { v.removeEventListener('loadedmetadata', seekOnce); if (hls) hls.destroy(); };
  }, [src, highQuality, maxBufferSeconds, resumeFrom]);

  // Play while active, pause otherwise. Cards mount only while hovered (active stays
  // true), so they play on mount; the hero stays mounted and toggles active on hover.
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    if (active) {
      v.volume = 1;
      v.muted = !AUDIO_UNLOCKED;   // unmuted once the user has interacted with the page
      const p = v.play();
      if (p && p.catch) p.catch((err) => {
        if (err && err.name === 'AbortError') return;   // interrupted by a pause — leave it
        v.muted = true;
        const pm = v.play();
        if (pm && pm.catch) pm.catch(() => {});
      });
    } else {
      v.pause();
    }
    return undefined;
  }, [active]);

  return (
    <video ref={ref} loop playsInline
      style={{
        position: 'absolute', inset: 0, width: '100%', height: '100%',
        objectFit: 'cover', objectPosition,
        opacity: active ? 1 : 0,
        transition: 'opacity 0.4s ease',
      }} />
  );
}

function Thumbnail({ video, colors, currentTier, showProgress, isHovered, isMobile }) {
  const { progressByVideo } = useAuth();
  const pct = videoPct(progressByVideo, video.videoId);
  const isLightTheme = tierIsLight(currentTier);
  
  return (
    <div style={{
      position: 'relative',
      aspectRatio: '16/9',
      borderRadius: isMobile ? '8px' : '10px',
      overflow: 'hidden',
      boxShadow: isHovered 
        ? `0 20px 40px -12px rgba(0,0,0,0.4), 0 0 0 1px ${colors.accent}30`
        : '0 8px 20px -8px rgba(0,0,0,0.3)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      transform: isHovered && !isMobile ? 'translateY(-4px)' : 'translateY(0)',
    }}>
      {video.thumbnailUrl
        ? <img src={video.thumbnailUrl} alt="" loading="lazy" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
        : <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(135deg, ${(video.gradient && video.gradient[0]) || '#667eea'} 0%, ${(video.gradient && video.gradient[1]) || '#764ba2'} 100%)` }} />}
      
      <div style={{
        position: 'absolute',
        inset: 0,
        background: `
          radial-gradient(circle at 30% 20%, rgba(255,255,255,0.2) 0%, transparent 50%),
          radial-gradient(circle at 70% 80%, rgba(0,0,0,0.2) 0%, transparent 50%)
        `,
        opacity: 0.8,
      }} />
      
      <div style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0,0,0,0.4)',
        opacity: isHovered || isMobile ? 1 : 0,
        transition: 'opacity 0.3s ease',
      }}>
        <div style={{
          width: isMobile ? '44px' : '56px',
          height: isMobile ? '44px' : '56px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          transform: isHovered || isMobile ? 'scale(1)' : 'scale(0.8)',
          transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          opacity: isMobile ? 0.9 : 1,
        }}>
          <svg width={isMobile ? 18 : 22} height={isMobile ? 18 : 22} viewBox="0 0 24 24" fill="#000" style={{ marginLeft: '2px' }}>
            <path d="M8 5v14l11-7z"/>
          </svg>
        </div>
      </div>

      {isHovered && video.previewUrl && <HoverPreview src={video.previewUrl} resumeFrom={videoResume(progressByVideo, video.videoId)} />}
      
      {(video.remaining || video.duration) && (
      <div style={{
        position: 'absolute',
        bottom: isMobile ? '8px' : '10px',
        right: isMobile ? '8px' : '10px',
        padding: isMobile ? '3px 6px' : '4px 8px',
        background: 'rgba(0,0,0,0.75)',
        backdropFilter: 'blur(4px)',
        borderRadius: '4px',
        fontSize: isMobile ? '10px' : '11px',
        fontFamily: '"DM Sans", sans-serif',
        color: '#fff',
        fontWeight: '500',
      }}>
        {video.remaining || video.duration}
      </div>
      )}
      
      {video.isNew && (
        <div style={{
          position: 'absolute',
          top: isMobile ? '8px' : '10px',
          left: isMobile ? '8px' : '10px',
          padding: isMobile ? '3px 8px' : '4px 10px',
          background: colors.accent,
          borderRadius: '4px',
          fontSize: isMobile ? '9px' : '10px',
          fontFamily: '"DM Sans", sans-serif',
          color: isLightTheme ? '#fff' : '#000',
          fontWeight: '700',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
        }}>
          New
        </div>
      )}
      
      {video.locked && <LockBadge isMobile={isMobile} />}
      {showProgress && pct > 0 && (
        <div style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: isMobile ? '3px' : '4px',
          background: 'rgba(0,0,0,0.5)',
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: colors.accent,
            boxShadow: `0 0 10px ${colors.accent}`,
            transition: 'width 0.3s ease',
          }} />
        </div>
      )}
    </div>
  );
}

function VideoCard({ video, colors, currentTier, showProgress = false, isHovered, onHover, onLeave, size = 'normal', isMobile, isTablet, onNavigate }) {
  const getWidth = () => {
    if (isMobile) return size === 'large' ? '280px' : '200px';
    if (isTablet) return size === 'large' ? '320px' : '240px';
    return size === 'large' ? '400px' : size === 'small' ? '200px' : '300px';
  };
  
  return (
    <div 
      onMouseEnter={onHover} 
      onMouseLeave={onLeave} 
      onClick={() => onNavigate && onNavigate(video)}
      style={{ flex: '0 0 auto', width: getWidth(), cursor: 'pointer' }}
    >
      <Thumbnail video={video} colors={colors} currentTier={currentTier} showProgress={showProgress} isHovered={isHovered} isMobile={isMobile} />
      <div style={{ marginTop: isMobile ? '10px' : '14px', paddingLeft: '2px' }}>
        <h3 style={{
          fontSize: isMobile ? '14px' : size === 'large' ? '17px' : '15px',
          fontWeight: '500',
          color: colors.text,
          fontFamily: '"DM Sans", sans-serif',
          margin: 0,
          lineHeight: 1.3,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}>
          {video.title}
        </h3>
        <p style={{ 
          fontSize: isMobile ? '12px' : '13px', 
          color: colors.muted, 
          fontFamily: '"DM Sans", sans-serif', 
          margin: '4px 0 0 0',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          flexWrap: 'wrap',
        }}>
          <span>{video.series}</span>
          {(video.reason || video.blurb) && (<span style={{ color: colors.accent, fontSize: isMobile ? '11px' : '12px' }}>{video.reason || video.blurb}</span>)}
        </p>
      </div>
    </div>
  );
}

function VideoRow({ title, subtitle, videos, colors, currentTier, showProgress = false, size = 'normal', isMobile, isTablet, onNavigate }) {
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const rowRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const isLightTheme = tierIsLight(currentTier);

  const scroll = (direction) => {
    if (rowRef.current) {
      const scrollAmount = rowRef.current.clientWidth * 0.75;
      rowRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const handleScroll = () => {
    if (rowRef.current) {
      setShowLeftArrow(rowRef.current.scrollLeft > 0);
      setShowRightArrow(rowRef.current.scrollLeft < rowRef.current.scrollWidth - rowRef.current.clientWidth - 10);
    }
  };

  useEffect(() => { handleScroll(); }, []);

  return (
    <section style={{ marginBottom: isMobile ? '36px' : '48px', overflow: 'hidden' }}>
      <div style={{ marginBottom: isMobile ? '16px' : '20px', paddingLeft: '4px' }}>
        <h2 style={{ fontSize: isMobile ? '20px' : '24px', fontWeight: '400', color: colors.text, fontFamily: '"Cormorant Garamond", serif', marginBottom: subtitle ? '4px' : 0 }}>{title}</h2>
        {subtitle && <p style={{ fontSize: isMobile ? '13px' : '14px', color: colors.muted, fontFamily: '"DM Sans", sans-serif' }}>{subtitle}</p>}
      </div>
      
      <div style={{ position: 'relative', margin: '0 -16px', padding: '0 16px' }}>
        {!isMobile && showLeftArrow && (
          <button onClick={() => scroll('left')} className="scroll-arrow" style={{
            position: 'absolute', left: '0', top: '50%', transform: 'translateY(-70%)', width: '44px', height: '44px', borderRadius: '50%',
            background: isLightTheme ? '#FFFFFF' : '#1A1A1A', border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)', cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2"><path d="M15 18l-6-6 6-6"/></svg>
          </button>
        )}
        
        {!isMobile && showRightArrow && (
          <button onClick={() => scroll('right')} className="scroll-arrow" style={{
            position: 'absolute', right: '0', top: '50%', transform: 'translateY(-70%)', width: '44px', height: '44px', borderRadius: '50%',
            background: isLightTheme ? '#FFFFFF' : '#1A1A1A', border: `1px solid ${isLightTheme ? 'rgba(0,0,0,0.1)' : 'rgba(255,255,255,0.1)'}`,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)', cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={colors.text} strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
          </button>
        )}
        
        <div ref={rowRef} onScroll={handleScroll} className="video-row-scroll" style={{
          display: 'flex', gap: isMobile ? '12px' : '20px', overflowX: 'auto', scrollbarWidth: 'none', msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch', paddingBottom: '8px', paddingLeft: '4px', paddingRight: '4px',
          marginLeft: isMobile ? '-16px' : '0', marginRight: isMobile ? '-16px' : '0',
        }}>
          {isMobile && <div style={{ minWidth: '12px', flexShrink: 0 }} />}
          {videos.map((video) => (
            <VideoCard key={video.videoId} video={video} colors={colors} currentTier={currentTier} showProgress={showProgress}
              isHovered={hoveredVideo === video.videoId} onHover={() => setHoveredVideo(video.videoId)} onLeave={() => setHoveredVideo(null)}
              size={size} isMobile={isMobile} isTablet={isTablet}
              onNavigate={onNavigate}
            />
          ))}
          {isMobile && <div style={{ minWidth: '12px', flexShrink: 0 }} />}
        </div>
      </div>
    </section>
  );
}



// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function LibraryPage({ 
  isMobile = false,
  isTablet = false,
  isDesktop = true,
  sidebarOpen = true,
}) {
  const { colors } = useTheme();
  const { profile, algorithm, catalog } = useAuth();
  const currentTier = profile?.currentTier || 'free';   // forwarded to sub-components as a prop
  const navigate = useNavigate();
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  // The algorithm feed carries no tier, so every card's lock comes from the node it
  // represents in the hierarchy (/catalog/hierarchy — the one source of truth). One
  // index, built once per catalog, keyed by kind and id. Every node's tier is read
  // from that node itself — course, chapter, section and topic (by videoId) alike;
  // the hierarchy decides the values, this code only reads them.
  // Anything not found (e.g. the catalog is still loading) counts as open; the video
  // page still gates playback on its own tier.
  const tierIndex = useMemo(() => {
    const m = new Map();
    // Only nodes with a real id are registered, so a card missing its id can never
    // match a node that is also missing one.
    const put = (kind, id, tier) => { if (id != null && id !== '' && !m.has(kind + ':' + id)) m.set(kind + ':' + id, tier); };
    (catalog || []).forEach((s) => (s.courses || []).forEach((c) => {
      put('course', c.id, c.tierRequired);
      (c.chapters || []).forEach((ch) => {
        put('chapter', ch.id, ch.tierRequired);
        (ch.sections || []).forEach((sec) => {
          put('section', sec.id, sec.tierRequired);
          (sec.topics || []).forEach((t) => put('topic', t.videoId, t.tierRequired));
        });
      });
    }));
    return m;
  }, [catalog]);
  // Each row names the kind of card it shows; the card's id for that kind is read
  // from the matching field.
  const ID_FIELD = { topic: 'videoId', course: 'courseId', chapter: 'chapterId', section: 'sectionId' };
  const withLock = (list, kind) => (list || []).map((item) => ({
    ...item,
    locked: isLocked(tierIndex.get(kind + ':' + item[ID_FIELD[kind]]), currentTier),
  }));
  // Every card carries videoId + courseId; route to the lesson with course context.
  const handleVideoNavigate = (item) => {
    if (item.locked) { setUpgradeOpen(true); return; }   // anything locked opens the prompt
    navigate(`/video/${item.videoId}${item.courseId ? `?course=${item.courseId}` : ''}`);
  };

  // The Library is entirely algorithm-driven (loaded on login, served from context).
  const recentlyWatched = withLock(algorithm?.recentlyWatched, 'topic');
  const suggestedNext = withLock(algorithm?.suggestedNext, 'topic');
  const nextCourses = withLock(algorithm?.nextCourses, 'course');
  const learnRandom = withLock(algorithm?.random, 'topic');


  return (
    <div style={{ 
      padding: isMobile ? '80px 16px 32px' : isTablet ? '80px 32px 32px' : '32px 48px', 
      minHeight: '100vh',
      width: '100%',
      maxWidth: '100%',
      overflowX: 'hidden',
      boxSizing: 'border-box',
      background: colors.bg,
      animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
    }}>
      {/* Search Bar */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: isMobile ? '28px' : '40px', marginTop: isMobile ? '0' : '16px' }}>
        <div style={{ width: '100%', maxWidth: '600px' }}>
          <SearchBar variant="library" placeholder="Search videos, instructors, topics..." />
        </div>
      </div>

      <HeroSection feature={recentlyWatched[0] || null} colors={colors} currentTier={currentTier} isMobile={isMobile} isTablet={isTablet} onNavigate={handleVideoNavigate} />

      {recentlyWatched.length > 0 && (
        <VideoRow title="Recently Watched" subtitle="Pick up where you left off" videos={recentlyWatched} colors={colors} currentTier={currentTier}
          showProgress={true} size="large" isMobile={isMobile} isTablet={isTablet} onNavigate={handleVideoNavigate} />
      )}

      {suggestedNext.length > 0 && (
        <VideoRow title="Suggested Next" subtitle="Based on what you've been watching" videos={suggestedNext} colors={colors} currentTier={currentTier}
          isMobile={isMobile} isTablet={isTablet} onNavigate={handleVideoNavigate} />
      )}

      {nextCourses.length > 0 && (
        <VideoRow title="Next Courses" subtitle="Explore something new" videos={nextCourses} colors={colors} currentTier={currentTier}
          size="large" isMobile={isMobile} isTablet={isTablet} onNavigate={handleVideoNavigate} />
      )}

      {learnRandom.length > 0 && (
        <VideoRow title="Learn Something Random" subtitle="A few topics from across the library" videos={learnRandom} colors={colors} currentTier={currentTier}
          size="large" isMobile={isMobile} isTablet={isTablet} onNavigate={handleVideoNavigate} />
      )}

      <div style={{ height: isMobile ? '32px' : '48px' }} />

      <UpgradePrompt open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
      <style>{`
        input::placeholder { color: ${colors.muted}; }
        .video-row-scroll::-webkit-scrollbar, .collection-videos::-webkit-scrollbar, .category-pills::-webkit-scrollbar { height: 0; width: 0; display: none; }
      `}</style>
    </div>
  );
}
